import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { sound } from "@/lib/sound";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import HeroReceiptScanner from "@/components/HeroReceiptScanner";

// Downscale + compress an image to a base64 JPEG (no data-URI prefix).
// Keeps the LONG edge large (receipts are tall + text is small when zoomed out)
// and steps quality/size down only if needed to stay under the server's cap.
async function fileToCompressedBase64(file, maxEdge = 1600, maxChars = 900_000) {
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });

  let edge = maxEdge;
  let quality = 0.72;
  let b64 = "";
  for (let attempt = 0; attempt < 6; attempt++) {
    const scale = Math.min(1, edge / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    b64 = canvas.toDataURL("image/jpeg", quality).split(",", 2)[1];
    if (b64.length <= maxChars) break;
    // Too big: trim quality first (keeps resolution/legibility), then dimension.
    if (quality > 0.62) quality -= 0.12;
    else edge = Math.round(edge * 0.85);
  }
  return b64;
}

// stable id for manual/scanned item rows (used as React key)
const rid = () =>
  window.crypto?.randomUUID?.() || `it_${Math.random().toString(36).slice(2)}`;

export default function CreateBill() {
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [name, setName] = useState("");
  const [merchant, setMerchant] = useState("");
  const [amount, setAmount] = useState("");
  const [items, setItems] = useState([]); // {name, price}
  const [tax, setTax] = useState(null); // scanned from receipt — source of truth, never estimated
  const [tipOn, setTipOn] = useState(false);
  const [tip, setTip] = useState("");
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);

  const hasItems = items.length > 0;

  // Warm the LLM vision route (TLS + model routing) the moment the Create
  // page opens, so the first receipt scan skips the cold-start penalty.
  // Best-effort & silent — never blocks or surfaces errors.
  useEffect(() => {
    api.get("/receipt/prewarm").catch(() => {});
  }, []);

  const itemsSum = items.reduce((s, it) => s + Number(it.price || 0), 0);
  const tipAmount = tipOn ? Number(tip || 0) : 0;
  // The receipt is the single source of truth: we include ONLY the tax that was
  // printed on it (never estimate or add extra tax). Tip is opt-in & manual.
  const computedTotal = hasItems
    ? itemsSum + Number(tax || 0) + tipAmount
    : Number(amount || 0) + tipAmount;

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    try {
      const b64 = await fileToCompressedBase64(file);
      const r = await api.post("/receipt/scan", { image_base64: b64 });
      const d = r.data;
      setItems((d.items || []).map((it) => ({ _id: rid(), name: it.name, price: it.price })));
      if (d.merchant) {
        setMerchant(d.merchant);
        if (!name) setName(d.merchant);
      }
      // Tax = exactly what the receipt shows (source of truth). We never add
      // extra/estimated tax on top. Tip stays manual (toggle below).
      setTax(d.tax ?? null);
      toast.success(`Scanned ${d.items?.length || 0} items`);
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Couldn't read that receipt — try again or add items manually"
      );
    } finally {
      setScanning(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const updateItem = (i, key, val) =>
    setItems(items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it)));
  const addItem = () => setItems([...items, { _id: rid(), name: "", price: "" }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!name.trim()) return toast.error("Give the bill a name");
    if (computedTotal <= 0) return toast.error("Add an amount or some items");
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        total_amount: Number(computedTotal.toFixed(2)),
        split_type: hasItems ? "by_item" : "even",
        merchant: merchant.trim() || null,
        items: hasItems
          ? items
              .filter((it) => it.name && Number(it.price) > 0)
              .map((it) => ({
                name: it.name,
                price: Number(it.price),
                assigned_to: [],
              }))
          : null,
        tax: tax != null ? Number(tax) : null,
        tip: tipOn && Number(tip) > 0 ? Number(tip) : null,
      };
      const r = await api.post("/bills", payload);
      const id = r.data.id || r.data.bill?.id;
      sound.play("success");
      toast.success("Bill created — share the invite link!");
      navigate(`/bill/${id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create bill");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-3xl font-extrabold text-white">New bill</h1>

      {/* Scan card */}
      <div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onPick}
          className="hidden"
          data-testid="receipt-file-input"
        />
        <HeroReceiptScanner
          onPress={() => fileRef.current?.click()}
          scanning={scanning}
        />
        <p className="text-xs text-muted-foreground text-center mt-2">
          Snap the receipt &mdash; we&apos;ll pull out the items automatically.
        </p>
      </div>

      {/* Details */}
      <div className="hp-glass rounded-3xl p-6 space-y-4">
        <div>
          <Label className="text-white">Bill name</Label>
          <Input
            data-testid="bill-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Dinner at Luigi's"
            className="mt-1.5 bg-white/5 border-white/10 text-white"
          />
        </div>
        <div>
          <Label className="text-white">Merchant (optional)</Label>
          <Input
            data-testid="bill-merchant-input"
            value={merchant}
            onChange={(e) => setMerchant(e.target.value)}
            placeholder="Luigi's Trattoria"
            className="mt-1.5 bg-white/5 border-white/10 text-white"
          />
        </div>

        {!hasItems && (
          <div>
            <Label className="text-white">Total amount</Label>
            <Input
              data-testid="bill-amount-input"
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="mt-1.5 bg-white/5 border-white/10 text-white"
            />
          </div>
        )}
      </div>

      {/* Items */}
      {hasItems && (
        <div className="hp-glass rounded-3xl p-6 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-white">Items</h3>
            <button
              data-testid="add-item-btn"
              onClick={addItem}
              className="flex items-center gap-1 text-sm text-[#c084fc] font-semibold"
            >
              <Plus size={16} /> Add item
            </button>
          </div>
          {items.map((it, i) => (
            <div key={it._id || i} className="flex items-center gap-2">
              <Input
                data-testid={`item-name-${i}`}
                value={it.name}
                onChange={(e) => updateItem(i, "name", e.target.value)}
                placeholder="Item"
                className="flex-1 bg-white/5 border-white/10 text-white"
              />
              <Input
                data-testid={`item-price-${i}`}
                type="number"
                step="0.01"
                value={it.price}
                onChange={(e) => updateItem(i, "price", e.target.value)}
                placeholder="0.00"
                className="w-24 bg-white/5 border-white/10 text-white"
              />
              <button
                onClick={() => removeItem(i)}
                className="text-muted-foreground hover:text-destructive p-2"
                data-testid={`remove-item-${i}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <div className="pt-2 border-t border-white/5 text-sm space-y-3">
            <div className="flex justify-between text-muted-foreground">
              <span>Subtotal</span>
              <span>{money(itemsSum)}</span>
            </div>

            {tax != null && (
              <div className="flex justify-between text-muted-foreground" data-testid="scanned-tax-row">
                <span>Tax (from receipt)</span>
                <span className="text-white">{money(tax)}</span>
              </div>
            )}

            {/* Tip — manual, off by default. Tax above is taken straight from
                the receipt; we never add extra tax on top of it. */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  data-testid="tip-toggle"
                  checked={tipOn}
                  onCheckedChange={(v) => {
                    sound.play("tap");
                    setTipOn(v);
                    if (!v) setTip("");
                  }}
                />
                <span className="text-white font-medium">Add a tip</span>
              </div>
              {tipOn && (
                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground">$</span>
                  <Input
                    data-testid="tip-input"
                    type="number"
                    step="0.01"
                    min="0"
                    inputMode="decimal"
                    autoFocus
                    value={tip}
                    onChange={(e) => setTip(e.target.value)}
                    placeholder="0.00"
                    className="w-24 bg-white/5 border-white/10 text-white text-right"
                  />
                </div>
              )}
            </div>

            {tipOn && Number(tip) > 0 && (
              <div className="flex justify-between text-muted-foreground">
                <span>Tip</span>
                <span className="text-white">{money(Number(tip))}</span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="hp-glass rounded-3xl p-5 flex items-center justify-between sticky bottom-24 z-20 bg-[#0e0a16]/95">
        <div>
          <p className="text-xs text-muted-foreground">Total</p>
          <span className="text-2xl font-extrabold text-white" data-testid="create-total">
            {money(computedTotal)}
          </span>
        </div>
        <Button
          data-testid="create-bill-submit"
          onClick={submit}
          disabled={busy}
          className="hp-cta h-12 px-8 rounded-xl font-bold text-white border-0"
        >
          {busy ? "Creating…" : "Create bill"}
        </Button>
      </div>
    </div>
  );
}
